import { useEffect, useMemo, useRef, useState } from "react";
import { usePageBuilder } from "../hooks";
import { useProject } from "../../project/context/ProjectContext";
import { DescriptionPopoverButton } from "../components/DescriptionPopoverButton";

type GjsBlock = {
  id: string;
  label?: string; // block.get('label') should populate this
  attributes?: Record<string, any>;
  category?: any;
};

const getDisplayLabel = (b: GjsBlock) => {
  const lbl = typeof b.label === "string" ? b.label.trim() : "";
  return lbl.length ? lbl : b.id; // fallback only if label is missing
};

const getBlockDescription = (block: any) => {
  const attrs = typeof block?.get === "function" ? block.get("attributes") : block?.attributes;
  return (attrs?.["data-description"] ?? "").toString();
};

function HelpTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;

  return (
    <span
      style={helpWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span style={helpIcon} aria-label="Description" role="img">?</span>

      {open && (
        <span style={tooltip} role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}

function normalizeCategory(cat: any): string {
  if (!cat) return "Uncategorized";
  if (typeof cat === "string") return cat;
  if (typeof cat === "object") return cat.label || cat.id || "Uncategorized";
  return "Uncategorized";
}

function groupByCategory(blocks: GjsBlock[]) {
  const map = new Map<string, GjsBlock[]>();
  blocks.forEach((b) => {
    const key = normalizeCategory(b.category);
    const arr = map.get(key) || [];
    arr.push(b);
    map.set(key, arr);
  });
  return map;
}

function sortCategoryKeys(keys: string[]) {
  const priority = (k: string) => {
    const s = k.toLowerCase();
    if (s.startsWith("basics")) return 0;
    if (s.startsWith("custom")) return 1;
    if (s.startsWith("template")) return 2;
    return 3;
  };
  return [...keys].sort((a, b) => priority(a) - priority(b) || a.localeCompare(b));
}

export function PageBuilderLeftPanel() {
  const { editorRef, isReady } = usePageBuilder();

  const { activeTemplateId } = useProject();

  const mountRef = useRef<HTMLDivElement | null>(null);
  const dragStartFnRef = useRef<any>(null);
  const dragStopFnRef = useRef<any>(null);

  const [blocks, setBlocks] = useState<GjsBlock[]>([]);
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    Basics: true,
    Custom: true,
  });

  const getBlockLabel = (block: any) => {
    const raw = typeof block?.get === "function" ? block.get("label") : block?.label;
    const label = String(raw ?? "").trim();
    return label.length ? label : String(block?.id ?? block?.get?.("id") ?? "");
  };


  useEffect(() => {
    const editor = editorRef.current;
    if (!isReady || !editor) return;

    const handleBlockCustom = (props: any) => {
      const { blocks, dragStart, dragStop } = props;

      dragStartFnRef.current = dragStart;
      dragStopFnRef.current = dragStop;

      setBlocks(blocks as GjsBlock[]);
    };

    editor.on("block:custom", handleBlockCustom);
    return () => {
      void editor.off("block:custom", handleBlockCustom);
    };
  }, [isReady, editorRef, activeTemplateId]);

  const filteredBlocks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return blocks;

    return blocks.filter((b) => {
      const label = getDisplayLabel(b).toLowerCase();
      const cat = normalizeCategory(b.category).toLowerCase();
      return label.includes(q) || cat.includes(q);
    });
  }, [blocks, query]);

  const grouped = useMemo(() => groupByCategory(filteredBlocks), [filteredBlocks]);
  const categories = useMemo(() => sortCategoryKeys([...grouped.keys() as any]), [grouped]);

  useEffect(() => {
    const templateKey = categories.find((k) => k.toLowerCase().startsWith("template"));
    if (templateKey && openSections[templateKey] === undefined) {
      setOpenSections((prev) => ({ ...prev, [templateKey]: true }));
    }
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const startDrag = (block: GjsBlock) => {
    if (dragStartFnRef.current) dragStartFnRef.current(block);
  };

  const endDrag = () => {
    if (dragStopFnRef.current) dragStopFnRef.current();
  };

  return (
    <aside style={sidebarStyle}>
      <div style={headerRow}>
        <h3 style={sidebarTitle}>Blocks</h3>
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search..."
        style={searchStyle}
      />

      <div ref={mountRef} style={containerStyle}>
        {categories.length === 0 ? (
          <div style={emptyState}>No blocks to show</div>
        ) : (
          categories.map((catKey) => {
            const list = grouped.get(catKey) || [];
            const isOpen = openSections[catKey] ?? true;

            return (
              <section key={catKey} style={sectionStyle}>
                <button type="button" onClick={() => toggle(catKey)} style={sectionHeader}>
                  <span style={sectionTitle}>{catKey}</span>
                  <span style={chevron}>{isOpen ? "▾" : "▸"}</span>
                </button>

                {isOpen && (
                  <div style={blocksGrid}>
                    {list.map((block) => {
                      const desc = getBlockDescription(block);

                      return (
                        <div
                          key={block.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            startDrag(block);
                          }}
                          onDragEnd={endDrag}
                          style={blockCard}
                          title={block.id}
                        >
                          <div style={blockHeaderRow}>
                            <div style={blockLabel}>{getBlockLabel(block)}</div>
                            <DescriptionPopoverButton text={desc} />
                          </div>

                          {/* Optional: still show a short preview line */}
                          {/* {desc ? <div style={blockMeta}>{desc}</div> : null} */}
                        </div>
                      );
                    })}

                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </aside>
  );
}

const sidebarStyle: React.CSSProperties = {
  width: 280,
  borderRight: "1px solid var(--fx-surface-border, #E2E8F0)",
  background: "var(--fx-surface-base, #FFFFFF)",
  padding: 16,
  overflowY: "auto",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
};

const sidebarTitle: React.CSSProperties = {
  fontWeight: 800,
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--fx-surface-text, #0F172A)",
  margin: 0,
};

const searchStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  background: "var(--fx-surface-base, #FFFFFF)",
  fontSize: 13,
  outline: "none",
  marginBottom: 12,
};

const containerStyle: React.CSSProperties = {
  minHeight: 300,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const sectionStyle: React.CSSProperties = {
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  borderRadius: 10,
  overflow: "hidden",
};

const sectionHeader: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px",
  background: "var(--fx-surface-sunken, #F8FAFC)",
  border: "none",
  cursor: "pointer",
};

const sectionTitle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--fx-surface-text, #0F172A)",
};

const chevron: React.CSSProperties = {
  fontSize: 14,
  color: "var(--fx-surface-textsecondary, #64748B)",
};

const blocksGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 8,
  padding: 10,
  background: "var(--fx-surface-base, #FFFFFF)",
};

const blockCard: React.CSSProperties = {
  padding: 12,
  background: "var(--fx-surface-muted, #F1F5F9)",
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  borderRadius: 8,
  cursor: "grab",
  userSelect: "none",
};

const blockLabel: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--fx-surface-text, #0F172A)",
  lineHeight: "16px",
};

const blockMeta: React.CSSProperties = {
  marginTop: 6,
  fontSize: 11,
  color: "var(--fx-surface-textsecondary, #64748B)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const emptyState: React.CSSProperties = {
  padding: 12,
  fontSize: 13,
  color: "var(--fx-surface-textsecondary, #64748B)",
  border: "1px dashed var(--fx-surface-border, #E2E8F0)",
  borderRadius: 10,
};

const blockHeaderRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};

const helpWrap: React.CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  zIndex: 999999999,
};

const helpIcon: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 999,
  border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
  background: "var(--fx-surface-base, #FFFFFF)",
  color: "var(--fx-surface-textsecondary, #64748B)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "16px",
  cursor: "help",
  userSelect: "none",
};

const tooltip: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 6px)",
  zIndex: 9999999999,
  minWidth: 180,
  maxWidth: 240,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  background: "#0f172a",
  color: "#fff",
  fontSize: 12,
  lineHeight: "16px",
  boxShadow: "0 10px 20px rgba(2, 6, 23, 0.18)",
  whiteSpace: "normal",
};

